import React, { useState } from 'react';
import useGameStore from '../engine/store';
import { getCreditScoreLabel, getCreditScoreColor } from '../engine/loanGenerator';

function formatMoney(value) {
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

export default function LoanCard({ loan }) {
  const { approveLoan, rejectLoan, liquidity } = useGameStore();
  const [rate, setRate] = useState(loan.suggestedRate);
  const [showDetails, setShowDetails] = useState(false);

  const creditInfo = getCreditScoreLabel(loan.creditScore);
  const creditColor = getCreditScoreColor(loan.creditScore);

  const canAfford = loan.amount <= liquidity;
  const isDecided = loan.approved !== null;

  // Calculate estimated monthly payment at current rate
  const r = rate / 12;
  const monthlyPayment = r > 0
    ? (loan.amount * r * Math.pow(1 + r, loan.duration)) / (Math.pow(1 + r, loan.duration) - 1)
    : loan.amount / loan.duration;
  const totalRepayment = monthlyPayment * loan.duration;
  const totalInterest = totalRepayment - loan.amount;

  // Risk visual indicator based on visible stats
  const dtiRisk = loan.debtToIncome > 4 ? 'high' : loan.debtToIncome > 2 ? 'medium' : 'low';
  const creditRisk = loan.creditScore < 550 ? 'high' : loan.creditScore < 700 ? 'medium' : 'low';

  const handleApprove = () => {
    approveLoan(loan.id, rate);
  };

  if (isDecided) {
    return (
      <div className={`glass-panel p-4 border-l-4 transition-all duration-300 ${
        loan.approved
          ? 'border-l-neon-green bg-neon-green/5 opacity-80'
          : 'border-l-neon-red bg-neon-red/5 opacity-60'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{loan.borrowerEmoji}</span>
            <div>
              <div className="font-semibold text-white text-sm">{loan.borrowerName}</div>
              <div className="text-xs text-gray-500">{formatMoney(loan.amount)} · {loan.duration}mo</div>
            </div>
          </div>
          <div className={`text-sm font-bold ${loan.approved ? 'text-neon-green' : 'text-neon-red'}`}>
            {loan.approved ? `✅ Approved @ ${(loan.playerRate * 100).toFixed(1)}%` : '❌ Rejected'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass-panel-hover overflow-hidden transition-all duration-300 ${
      !canAfford ? 'opacity-50' : ''
    }`}>
      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={() => setShowDetails(!showDetails)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{loan.borrowerEmoji}</div>
            <div>
              <div className="font-bold text-white">{loan.borrowerName}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{
                  background: `${loan.borrowerColor}20`,
                  color: loan.borrowerColor,
                }}>
                  {loan.borrowerLabel}
                </span>
                <span className="text-xs text-gray-500">{loan.purpose}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-black text-white font-mono">{formatMoney(loan.amount)}</div>
            <div className="text-xs text-gray-500">{loan.duration} months</div>
          </div>
        </div>

        {/* Quick stats row */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          <div>
            <div className="text-xs text-gray-500">Credit</div>
            <div className="text-sm font-bold font-mono" style={{ color: creditColor }}>
              {loan.creditScore}
            </div>
            <div className="text-xs" style={{ color: creditColor }}>{creditInfo.label}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Income</div>
            <div className="text-sm font-bold font-mono text-white">{formatMoney(loan.monthlyIncome)}/mo</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">DTI Ratio</div>
            <div className={`text-sm font-bold font-mono ${
              dtiRisk === 'high' ? 'text-neon-red' : dtiRisk === 'medium' ? 'text-neon-yellow' : 'text-neon-green'
            }`}>
              {loan.debtToIncome.toFixed(1)}x
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Suggested</div>
            <div className="text-sm font-bold font-mono text-neon-blue">
              {(loan.suggestedRate * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {showDetails && (
        <div className="border-t border-white/5 p-4 animate-slide-up space-y-4">
          {/* Risk indicators */}
          <div className="grid grid-cols-2 gap-3">
            <div className="glass-panel p-3">
              <div className="text-xs text-gray-500 mb-1">Credit Risk</div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  creditRisk === 'high' ? 'bg-neon-red animate-pulse' :
                  creditRisk === 'medium' ? 'bg-neon-yellow' : 'bg-neon-green'
                }`} />
                <span className="text-sm text-gray-300 capitalize">{creditRisk}</span>
              </div>
            </div>
            <div className="glass-panel p-3">
              <div className="text-xs text-gray-500 mb-1">Debt Burden</div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  dtiRisk === 'high' ? 'bg-neon-red animate-pulse' :
                  dtiRisk === 'medium' ? 'bg-neon-yellow' : 'bg-neon-green'
                }`} />
                <span className="text-sm text-gray-300 capitalize">{dtiRisk}</span>
              </div>
            </div>
          </div>

          {/* Rate selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Your Interest Rate</span>
              <span className="text-lg font-black font-mono text-neon-yellow">
                {(rate * 100).toFixed(1)}%
              </span>
            </div>
            <input
              type="range"
              min="2"
              max="30"
              step="0.5"
              value={rate * 100}
              onChange={(e) => setRate(parseFloat(e.target.value) / 100)}
              className="slider-custom w-full"
              style={{
                background: `linear-gradient(to right, #00ff88 0%, #ffcc00 50%, #ff3366 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>2%</span>
              <span>15%</span>
              <span>30%</span>
            </div>
          </div>

          {/* Projected returns */}
          <div className="glass-panel p-3">
            <div className="text-xs text-gray-500 mb-2">Projected Returns</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-gray-500">Monthly</div>
                <div className="text-sm font-mono text-neon-green">{formatMoney(monthlyPayment)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Interest</div>
                <div className="text-sm font-mono text-neon-yellow">{formatMoney(totalInterest)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Back</div>
                <div className="text-sm font-mono text-white">{formatMoney(totalRepayment)}</div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleApprove}
              disabled={!canAfford}
              className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 ${
                canAfford
                  ? 'bg-neon-green/20 border border-neon-green/40 text-neon-green hover:bg-neon-green/30'
                  : 'bg-dark-600 border border-white/5 text-gray-600 cursor-not-allowed'
              }`}
            >
              {canAfford ? '✅ Approve Loan' : '💸 Insufficient Funds'}
            </button>
            <button
              onClick={() => rejectLoan(loan.id)}
              className="flex-1 py-3 rounded-xl font-bold text-sm bg-neon-red/10 border border-neon-red/30 text-neon-red hover:bg-neon-red/20 transition-all active:scale-95"
            >
              ❌ Reject
            </button>
          </div>

          {rate > loan.suggestedRate * 1.5 && (
            <div className="text-xs text-neon-orange bg-neon-orange/10 rounded-lg p-2 text-center">
              ⚠️ High rate increases default risk — borrower may struggle to repay
            </div>
          )}
        </div>
      )}
    </div>
  );
}
